import { useState } from 'react';
import { useRecoilValue } from 'recoil';
import { saveAs } from 'file-saver';

import { useNotification } from 'hooks/useNotification';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { API_ROUTES } from 'lib/api-routes';
import { CandidateApplicationAIResponse } from 'lib/models/candidate-applications';
import { parseErrorResponse } from 'lib/utils/parser';
import { apiInstance } from 'lib/utils/axios';

export interface CoverLetterJobDataType {
  title: string;
  description: string;
  company_name: string;
}

export interface ReturnType {
  isLoading: boolean;
  generateCoverLetter: (
    touchPointId: string,
    jobData?: CoverLetterJobDataType,
    currentCoverLetter?: string
  ) => Promise<CandidateApplicationAIResponse | undefined>;
  generateQuestionReply: (
    touchPointId: string,
    question: string
  ) => Promise<CandidateApplicationAIResponse | undefined>;
  downloadCv: (info: {
    first_name: string;
    last_name: string;
  }) => Promise<void>;
  abortGenerateQuestionReply: () => void;
  exportCoverLetterPDF: (cover_letter: string, companyName?: string) => void;
  copyCoverLetter: (data: string) => void;
}

export const useAIGeneratedResponse = (): ReturnType => {
  const { headers, plainHeaders } = useRecoilValue(httpHeadersState);
  const notificationInstance = useNotification();
  const { NEXT_PUBLIC_API_HOST_URL } = process.env;

  const [abortController, setAbortController] = useState<
    AbortController | undefined
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const generateCoverLetter = async (
    touchPointId: string,
    jobData?: CoverLetterJobDataType,
    currentCoverLetter?: string
  ) => {
    const path = API_ROUTES.CANDIDATE_COVER_LETTER.replace(
      '/[id]',
      touchPointId ? `/${touchPointId}` : ''
    );
    const body = touchPointId ? {} : jobData;
    setIsLoading(true);
    try {
      const response = await apiInstance.post<CandidateApplicationAIResponse>(
        path,
        { ...body, current_cover_letter: currentCoverLetter }
      );
      return response.data;
    } catch (error) {
      notificationInstance.handleExceptionError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateQuestionReply = async (
    touchPointId: string,
    question: string
  ) => {
    const controller = new AbortController();
    setAbortController(controller);
    const url = API_ROUTES.CANDIDATE_QUESTION_REPLY;
    const path = touchPointId
      ? url.replace('[id]', touchPointId)
      : url.replace('/touchpoints/[id]', '');
    const body = {
      question,
    };
    const args = {
      method: 'POST',
      body: JSON.stringify(body),
      headers,
      signal: controller.signal,
    };
    const request = new Request(`${NEXT_PUBLIC_API_HOST_URL}${path}`, args);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await fetch(request);
      return response.json();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const err = parseErrorResponse(error);
      if (err !== 'The user aborted a request.') {
        throw new Error(error.alert || error.error);
      }
    }
  };

  const downloadCv = async (info: {
    first_name: string;
    last_name: string;
  }) => {
    const path = API_ROUTES.CANDIDATE_DOWNLOAD_CV;
    plainHeaders.set('Content-Type', 'application/pdf');
    plainHeaders.set('Accept', 'application/pdf');
    plainHeaders.set('Response-Type', 'application/pdf');
    const args = {
      method: 'POST',
      headers: plainHeaders,
    };

    const request = new Request(`${NEXT_PUBLIC_API_HOST_URL}${path}`, args);
    try {
      const response = await fetch(request);
      const blob = await response.blob();
      const fileURL = window.URL.createObjectURL(blob);
      const fileName = `${info.first_name}-${info.last_name}-CV.pdf`;
      await saveAs(fileURL, fileName);
    } catch (error) {
      notificationInstance.handleExceptionError(error as Error);
    }
  };

  const abortGenerateQuestionReply = () => {
    if (abortController) {
      abortController.abort();
      setAbortController(undefined);
    }
  };

  const exportCoverLetterPDF = async (
    cover_letter: string,
    companyName?: string
  ) => {
    const path = `${API_ROUTES.CANDIDATE_TOUCHPOINT}/cover_letter_pdf`;
    const body = {
      cover_letter,
    };
    try {
      setIsLoading(true);
      const response = await apiInstance.post(path, body, {
        responseType: 'blob',
      });
      const blob = response.data;
      const fileURL = window.URL.createObjectURL(blob);
      const fileName = `${
        companyName ? `${companyName}-` : ''
      }cover-letter.pdf`;
      saveAs(fileURL, fileName);
    } catch (error) {
      notificationInstance.handleExceptionError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const copyCoverLetter = (data: string) => {
    navigator.clipboard.writeText(data);
    notificationInstance.success({
      title: 'Cover letter copied',
      message: 'AI generated cover letter has been copied to your clipboard!',
    });
  };

  return {
    isLoading,
    generateCoverLetter,
    generateQuestionReply,
    downloadCv,
    abortGenerateQuestionReply,
    exportCoverLetterPDF,
    copyCoverLetter,
  };
};
